import { CircleNode, LineNode } from '../Node';
import Effect from '../Effect/EffectLine';
import { fixedNumber } from '../../utils/common';
import { FUN_TYPE, EFFECT_TAG } from '../../enums/index';

/**
 * 图片绘制基础组件
 */
class BaseMode {
  constructor(props) {
  }

  initProps(props = {}) {
    this.data = {
      ...this.data,
      ...props
    }

    // 保存事件监听器引用，用于后续清理
    this._eventListeners = [];
  }

  // 注册事件监听器（带追踪）
  addTrackedEventListener(target, event, handler, options) {
    target.addEventListener(event, handler, options);
    this._eventListeners.push({ target, event, handler, options });
  }

  // 销毁方法 - 清理所有事件监听器
  destroy() {
    this._eventListeners.forEach(({ target, event, handler }) => {
      if (target && typeof target.removeEventListener === 'function') {
        target.removeEventListener(event, handler);
      }
    });
    this._eventListeners = [];

    if (this.data?.stage) {
      this.data.stage.off();
    }

    const drawLayerMap = this.data?.drawLayer;
    if (drawLayerMap) {
      drawLayerMap.forEach((layer) => {
        if (layer?.stateNode) {
          layer.stateNode.off();
          layer.stateNode.destroyChildren && layer.stateNode.destroyChildren();
          layer.stateNode.destroy && layer.stateNode.destroy();
          layer.stateNode = null;
        }
        if (layer?.lampNodeQueue) {
          layer.lampNodeQueue.clear();
          layer.lampNodeQueue.render = null;
          layer.lampNodeQueue = null;
        }
        if (layer?.lineNodeQuene) {
          layer.lineNodeQuene.clear();
          layer.lineNodeQuene.render = null;
          layer.lineNodeQuene = null;
        }
        if (layer?.circlePointerNode) {
          layer.circlePointerNode = null;
        }
        if (layer?.circleNode) {
          layer.circleNode = null;
        }
        if (layer?.groups) {
          layer.groups.clear();
          layer.groups = null;
        }
      });
      drawLayerMap.clear();
    }

    this.data.onActive = null;
    this.data.btnInstance = null;
    this.data.canvasContent = null;
    this.data.config = null;
    this.data.curMovePointer = null;
    this.data = null;
  }

  // 获取绘制的图像参数
  mergeDrawOption(drawOption, defaultOption) {
    if (!!drawOption) {
      return Object.assign({}, defaultOption, drawOption)
    }

    return defaultOption;
  }

  // 设置当前图层的同时暗淡所有其余图层 用于初始化数据
  setCurLineLayerIndexWithAllOpacity(mapId) {
    const drawLayerMap = this.data.drawLayer;

    drawLayerMap.forEach(drawLayer => {
      drawLayer.stateNode.opacity(0.3);
      drawLayer.render();
    })

    this.setCurLineLayerIndex(mapId);
  }

  // 设置当前图层的同时暗淡上个图层 用于图层间切换
  setCurLineLayerIndex(mapId, isShowNum = false) {
    const preDrawLayer = this.getCurDrawLayer() || {};

    // 和原来的一致 不做任何行为
    if (mapId === preDrawLayer.mapId) return;

    if (preDrawLayer.mapId) {
      preDrawLayer.stateNode.opacity(0.3);
      preDrawLayer.render();
    };

    this.data.curDrawLayerId = mapId;

    const curDrawLayer = this.getCurDrawLayer();

    if (!curDrawLayer) {
      // 没有该图层会进行创建
      this.createDrawLayer(mapId);
    } else {
      const idMatch = mapId.match(/\d+-(\d+)/);

      if (idMatch && isShowNum) {
        const shuangluIndex = idMatch[1];

        const lastNum = document.getElementById('shuanglu-num');

        const num = document.createElement('div');

        num.id = 'shuanglu-num';
        num.innerHTML = Number(shuangluIndex) + 1;
        num.style.position = 'fixed';
        num.style.top = '40%';
        num.style.left = '48%';
        num.style.zIndex = 9999;
        num.style.color = '#eee'; // '#00278e';
        num.style.fontSize = '100px';

        if (lastNum) {
         document.body.removeChild(lastNum);
        }

        document.body.appendChild(num);

        setTimeout(() => {
          try {
           document.body.removeChild(num);
          } catch (e) {
            console.log(e);
          }
        }, 1500);
      };
      
      curDrawLayer.stateNode.opacity(1);
      curDrawLayer.render();
    }

    if (this.data.btnInstance) {
      this.data.btnInstance.setIsCanDraw(false);
    }
  }

  // 获取当前激活的绘画图层 如果传递对应id则使用id的数据
  getCurDrawLayer = (curDrawLayerId) => {
    const id = curDrawLayerId || this.data.curDrawLayerId;

    return this.data.drawLayer.get(id);
  }

  getDrawLayerMap = () => {
    return this.data.drawLayer;
  }
  
  /**
   * 创建圆
   * @param {*} rendrenData x y 坐标
   * @param {*} funType 业务类别 eg: 点 线 灯
   * @param {*} rendrenOption 渲染参数 红色圈 or 蓝色圈
   */
  createCircle = (rendrenData, funType, rendrenOption = {}) => {
    return new CircleNode(
      rendrenData,
      funType,
      rendrenOption
    );
  }

  /**
   * 创建单线
   * @param {*} rendrenData startX startY endX endY 坐标 等 startSize参数非必传 用于有前后关系的单线 可以计算当前线总长
   * @param {*} funType 业务类别 eg: 点 线 灯
   * @param {*} rendrenOption 渲染参数 红色线 or 蓝色线
   */
  createSingleLine = (rendrenData, funType, rendrenOption = {}) => {
    return new LineNode(
      rendrenData,
      funType,
      rendrenOption
    );
  }

  /**
   * 增加灯位信息 用于每条线灯位个数不一样
   */
  setLampInfo = (circleInfoList) => {
    if (circleInfoList && circleInfoList.vDeviceId) {
      const { lampNodeQueue } = this.getCurDrawLayer(circleInfoList.vDeviceId);

      lampNodeQueue.setCircleInfoList(circleInfoList.list);
    } else {
      const { lampNodeQueue } = this.getCurDrawLayer();

      lampNodeQueue.setCircleInfoList(circleInfoList);
    }
    
  }

  /**
   * 增加灯
   * 此处模拟react 对新老key做一层对比逻辑
   * 尽可能利用老节点move 不够用时才创建新的 如新节点变少了 删除旧node
   * @param {*} num 数量
   * @param {*} isRender 生成副作用后是否需要渲染
   * @returns 
   */
  addLamp = (num, isRender = true) => {
    const drawLayer = this.getCurDrawLayer();
    const rendrenOption = this.data.rendrenOption;
    const lampNodeQueue = drawLayer.lampNodeQueue;

    const lampXyByCurLayerData = this.getLampXyByCurLayer(num); // 获取灯的位置

    // 尚无有效灯位（例如仅有一个路径点、尚未成线）时，清理残留灯节点避免对 null stateNode 执行销毁
    if (!lampXyByCurLayerData) {
      if (lampNodeQueue.firstNode) {
        this.clearLampNodeQueue(isRender);
      }
      return;
    }

    let curNode = lampNodeQueue.firstNode;
    let curData = lampXyByCurLayerData;
// console.log('lampNodeQueue', lampNodeQueue);

    // 遍历老节点
    // 原来灯的数据和本次数据 竞争关系 curNode item 任何一条数据链先用完 都停下 
    while (curNode && curData) {
      curNode.flags |= EFFECT_TAG.POSITION;
      curNode.effects.push(new Effect({
        flag: EFFECT_TAG.POSITION,
        // 低优先级任务 此处不要打开注释 测试用
        // level: EFFECT_LEVEL.LOW,
        payload: {
          x: fixedNumber(curData.x),
          y: fixedNumber(curData.y),
        }
      }))
  
      // console.log('命中 移动');
      curNode = curNode.next;
      curData = curData.next;
    }

    // 新老node数量相等 最优情况
    if (!curData && !curNode) {
      // console.log('命中相等');
      lampNodeQueue.flushEffectNode();

      return;
    }

    // 老的节点走完了 新的还没走完 说明加的灯比之前的多 需要添加新的
    if (!curNode && curData) {
      // console.log('命中新增');

      while (curData) {
        const insertPointer = this.createCircle({
          x: fixedNumber(curData.x),
          y: fixedNumber(curData.y),
        }, FUN_TYPE.LAMP, rendrenOption.lamp)

        drawLayer.add(insertPointer);
        lampNodeQueue.push(insertPointer);
        curData = curData.next;
      }
      
      lampNodeQueue.flushEffectNode(false);

      // 由于老的走动过位置 所以此处一次性渲染【移动的和新增的】
      isRender && drawLayer.render();

      return;
    }

    // console.log('命中删除');
    // 新的走完了 老的还没走完 需要删除老的 [item优先用完的情况]
    while (curNode) {
      // flushEffectNode时进行删除
      curNode.flags |= EFFECT_TAG.DELETE;

      curNode = curNode.next;
    }

    lampNodeQueue.flushEffectNode(false);

    // 由于老的走动过位置 所以此处一次性渲染【移动的和删除的】
    isRender && drawLayer.render();
  }

    // 清除所有灯
    clearLampNodeQueue(isRender = true) {
      const drawLayer = this.getCurDrawLayer();
      const { lampNodeQueue } = drawLayer;
  
      let lampNode = lampNodeQueue.firstNode;
  
      while (lampNode) {
        // flushEffectNode时删除
        lampNode.flags |= EFFECT_TAG.DELETE;
  
        lampNode = lampNode.next;
      }

      // 处理所有副作用 并且渲染
      lampNodeQueue.flushEffectNode(isRender);
    }

  // 渲染当前图层
  render = () => {
    this.getCurDrawLayer().render();
  }

  // 渲染所有挂图层
  renderAllLayer = () => {
    const drawLayerMap = this.data.drawLayer;

    drawLayerMap.forEach((drawLayer) => {
      drawLayer.render();
    });
  }
}

export default BaseMode;